import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Upload, Eye, Download, Save, Check, X, FileText } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import AnteprimaDocumento, { haAnteprima } from '../../components/AnteprimaDocumento';
import { nomeDownload } from '../../utils/documentiDipendente';

// Contratti strutture: un contenitore per sede (costi personalizzabili, file del contratto, note libere).
const MAX_MB = 10;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.docx,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const euro = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const kb = (b) => b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const errore = (e) => alert(e.response?.data?.error || 'Errore');
const VOCE_VUOTA = { etichetta: '', importo: '', dettaglio: '' };

export default function ContrattiStrutture() {
  const { user } = useAuth();
  const puoCreare = puoFare(user, 'strutture', 'create');
  const puoModificare = puoFare(user, 'strutture', 'edit');
  const puoEliminare = puoFare(user, 'strutture', 'delete');

  const [strutture, setStrutture] = useState([]);
  const [selId, setSelId] = useState(null);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState('');
  const [noteSalvate, setNoteSalvate] = useState('');
  const [salvandoNote, setSalvandoNote] = useState(false);

  const [testata, setTestata] = useState(null);      // { nome, indirizzo } quando in modifica
  const [nuova, setNuova] = useState(null);          // { nome, indirizzo } per creare
  const [voceForm, setVoceForm] = useState(null);    // { id?: number, etichetta, importo, dettaglio }
  const [anteprima, setAnteprima] = useState(null);
  const [caricando, setCaricando] = useState(false);
  const fileRef = useRef(null);

  const noteModificate = note !== noteSalvate;

  const caricaElenco = useCallback(async () => {
    const r = await api.get('/strutture');
    setStrutture(r.data);
    setSelId(id => id && r.data.some(s => s.id === id) ? id : r.data[0]?.id ?? null);
  }, []);

  const caricaDettaglio = useCallback(async (id, aggiornaNote = false) => {
    if (!id) { setSel(null); return; }
    const r = await api.get(`/strutture/${id}`);
    setSel(r.data);
    if (aggiornaNote) { setNote(r.data.note || ''); setNoteSalvate(r.data.note || ''); }
  }, []);

  useEffect(() => { caricaElenco().catch(console.error); }, [caricaElenco]);
  useEffect(() => { caricaDettaglio(selId, true).catch(console.error); setTestata(null); setVoceForm(null); }, [selId, caricaDettaglio]);

  // Avviso se si chiude la pagina con note non salvate
  useEffect(() => {
    const h = (e) => { if (noteModificate) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [noteModificate]);

  const cambiaStruttura = (id) => {
    if (id === selId) return;
    if (noteModificate && !window.confirm('Le note non sono salvate. Cambiare struttura e perderle?')) return;
    setSelId(id);
  };

  const ricarica = () => Promise.all([caricaDettaglio(selId), caricaElenco()]);

  // --- Strutture ---
  const creaStruttura = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post('/strutture', nuova);
      setNuova(null);
      await caricaElenco();
      setSelId(r.data.id);
    } catch (err) { errore(err); }
  };

  const salvaTestata = async (e) => {
    e.preventDefault();
    try { await api.put(`/strutture/${sel.id}`, testata); setTestata(null); await ricarica(); }
    catch (err) { errore(err); }
  };

  const eliminaStruttura = async () => {
    if (!window.confirm(`Eliminare la struttura "${sel.nome}" con tutte le voci, i documenti caricati e le note? Non si può annullare.`)) return;
    try { await api.delete(`/strutture/${sel.id}`); setSelId(null); await caricaElenco(); }
    catch (err) { errore(err); }
  };

  // --- Note ---
  const salvaNote = async () => {
    setSalvandoNote(true);
    try { await api.put(`/strutture/${sel.id}`, { note }); setNoteSalvate(note); }
    catch (err) { errore(err); }
    finally { setSalvandoNote(false); }
  };

  // --- Voci di costo ---
  const salvaVoce = async (e) => {
    e.preventDefault();
    const { id, ...dati } = voceForm;
    try {
      if (id) await api.put(`/strutture/${sel.id}/voci/${id}`, dati);
      else await api.post(`/strutture/${sel.id}/voci`, dati);
      setVoceForm(null); await caricaDettaglio(sel.id);
    } catch (err) { errore(err); }
  };

  const eliminaVoce = async (v) => {
    if (!window.confirm(`Eliminare la voce "${v.etichetta}"?`)) return;
    try { await api.delete(`/strutture/${sel.id}/voci/${v.id}`); await caricaDettaglio(sel.id); }
    catch (err) { errore(err); }
  };

  const spostaVoce = async (v, direzione) => {
    try { await api.patch(`/strutture/${sel.id}/voci/${v.id}/sposta`, { direzione }); await caricaDettaglio(sel.id); }
    catch (err) { errore(err); }
  };

  // --- Documenti ---
  const caricaFile = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) return alert(`File troppo grande (massimo ${MAX_MB} MB)`);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setCaricando(true);
      try {
        await api.post(`/strutture/${sel.id}/documenti`, {
          fileData: ev.target.result.split(',')[1], fileName: file.name, nome: file.name.replace(/\.[^.]+$/, ''),
        });
        await ricarica();
      } catch (err) { errore(err); }
      finally { setCaricando(false); }
    };
    reader.readAsDataURL(file);
  };

  const rinominaDoc = async (d) => {
    const nome = window.prompt('Nome del documento', d.nome);
    if (!nome || nome.trim() === d.nome) return;
    try { await api.patch(`/strutture/${sel.id}/documenti/${d.id}`, { nome }); await caricaDettaglio(sel.id); }
    catch (err) { errore(err); }
  };

  const eliminaDoc = async (d) => {
    if (!window.confirm(`Eliminare il documento "${d.nome}"?`)) return;
    try { await api.delete(`/strutture/${sel.id}/documenti/${d.id}`); await ricarica(); }
    catch (err) { errore(err); }
  };

  const scaricaDoc = async (d) => {
    try {
      const r = await api.get(`/strutture/${sel.id}/documenti/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = nomeDownload(d); a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { alert('Impossibile scaricare il documento'); }
  };

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>CONTRATTI STRUTTURE</h1>
          <p style={s.sub}>Per ogni sede: i costi concordati con la struttura, i file del contratto e le tue note.</p>
        </div>
        {puoCreare && <button style={s.btnPri} onClick={() => setNuova({ nome: '', indirizzo: '' })}><Plus size={14} /> NUOVA STRUTTURA</button>}
      </div>

      <div style={s.pills}>
        {strutture.map(st => (
          <button key={st.id} style={{ ...s.pill, ...(st.id === selId ? s.pillOn : {}) }} onClick={() => cambiaStruttura(st.id)}>
            {st.nome}
            {st._count.documenti > 0 && <span style={s.pillCount}><FileText size={10} /> {st._count.documenti}</span>}
          </button>
        ))}
        {strutture.length === 0 && <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)' }}>Nessuna struttura.</span>}
      </div>

      {sel && (
        <div style={s.grid}>
          {/* TESTATA */}
          <div style={{ ...s.card, gridColumn: '1 / -1' }}>
            {testata ? (
              <form onSubmit={salvaTestata} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ ...s.field, flex: '1 1 200px' }}><label style={s.label}>NOME</label><input style={s.input} value={testata.nome} onChange={e => setTestata(t => ({ ...t, nome: e.target.value }))} required autoFocus /></div>
                <div style={{ ...s.field, flex: '2 1 280px' }}><label style={s.label}>INDIRIZZO</label><input style={s.input} value={testata.indirizzo} onChange={e => setTestata(t => ({ ...t, indirizzo: e.target.value }))} /></div>
                <button type="button" style={s.btnSec} onClick={() => setTestata(null)}>ANNULLA</button>
                <button type="submit" style={s.btnPri}><Check size={13} /> SALVA</button>
              </form>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <div style={s.nomeSede}>{sel.nome}</div>
                  <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)' }}>{sel.indirizzo || 'Indirizzo non inserito'}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {puoModificare && <button style={s.iconBtn} onClick={() => setTestata({ nome: sel.nome, indirizzo: sel.indirizzo || '' })}><Pencil size={13} />&nbsp;MODIFICA</button>}
                  {puoEliminare && <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={eliminaStruttura} title="Elimina struttura"><Trash2 size={13} /></button>}
                </div>
              </div>
            )}
          </div>

          {/* COSTI DEDICATI (business plan) */}
          <CostiCampi key={sel.id} sel={sel} puoModificare={puoModificare} onSalvato={() => caricaDettaglio(sel.id)} />

          {/* COSTI */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>ALTRE VOCI <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', letterSpacing: 0, fontWeight: 600 }}>· solo promemoria</span></div>
              {puoModificare && !voceForm && <button style={s.iconBtn} onClick={() => setVoceForm({ ...VOCE_VUOTA })}><Plus size={13} />&nbsp;VOCE</button>}
            </div>
            {sel.voci.length === 0 && !voceForm && <div style={s.vuoto}>Nessuna voce. Qui puoi annotare altri costi o accordi (es. illuminazione, magazzino): non entrano nei calcoli.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {sel.voci.map((v, i) => voceForm?.id === v.id ? (
                <FormVoce key={v.id} voce={voceForm} setVoce={setVoceForm} onSubmit={salvaVoce} />
              ) : (
                <div key={v.id} style={s.riga}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{v.etichetta}</div>
                    {v.dettaglio && <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>{v.dettaglio}</div>}
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 'var(--font-size-sm)', color: v.importo == null ? 'var(--text-dim)' : '#F4600C', whiteSpace: 'nowrap' }}>{euro(v.importo)}</div>
                  {puoModificare && (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button style={s.miniBtn} disabled={i === 0} onClick={() => spostaVoce(v, 'su')} title="Sposta su"><ChevronUp size={12} /></button>
                      <button style={s.miniBtn} disabled={i === sel.voci.length - 1} onClick={() => spostaVoce(v, 'giu')} title="Sposta giù"><ChevronDown size={12} /></button>
                      <button style={s.miniBtn} onClick={() => setVoceForm({ id: v.id, etichetta: v.etichetta, importo: v.importo ?? '', dettaglio: v.dettaglio || '' })} title="Modifica"><Pencil size={12} /></button>
                      <button style={{ ...s.miniBtn, color: '#EF4444' }} onClick={() => eliminaVoce(v)} title="Elimina"><Trash2 size={12} /></button>
                    </div>
                  )}
                </div>
              ))}
              {voceForm && !voceForm.id && <FormVoce voce={voceForm} setVoce={setVoceForm} onSubmit={salvaVoce} />}
            </div>
          </div>

          {/* DOCUMENTI */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>DOCUMENTI</div>
              {puoModificare && (
                <>
                  <input ref={fileRef} type="file" accept={ACCEPT} style={{ display: 'none' }} onChange={caricaFile} />
                  <button style={s.iconBtn} disabled={caricando} onClick={() => fileRef.current.click()}><Upload size={13} />&nbsp;{caricando ? 'CARICAMENTO…' : 'CARICA'}</button>
                </>
              )}
            </div>
            {sel.documenti.length === 0 && <div style={s.vuoto}>Nessun file. PDF, JPG, PNG o DOCX, massimo {MAX_MB} MB.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {sel.documenti.map(d => (
                <div key={d.id} style={s.riga}>
                  <FileText size={16} color="#F4600C" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: haAnteprima(d) ? 'pointer' : 'default' }}
                      onClick={() => haAnteprima(d) && setAnteprima(d)}>{d.nome}</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>
                      {d.file_nome.split('.').pop().toUpperCase()} · {kb(d.dimensione)} · {new Date(d.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {haAnteprima(d) && <button style={s.miniBtn} onClick={() => setAnteprima(d)} title="Visualizza"><Eye size={12} /></button>}
                    <button style={s.miniBtn} onClick={() => scaricaDoc(d)} title="Scarica"><Download size={12} /></button>
                    {puoModificare && <button style={s.miniBtn} onClick={() => rinominaDoc(d)} title="Rinomina"><Pencil size={12} /></button>}
                    {puoModificare && <button style={{ ...s.miniBtn, color: '#EF4444' }} onClick={() => eliminaDoc(d)} title="Elimina"><Trash2 size={12} /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* NOTE */}
          <div style={{ ...s.card, gridColumn: '1 / -1' }}>
            <div style={s.cardHead}>
              <div style={s.cardTitle}>NOTE {noteModificate && <span style={{ color: '#F59E0B', fontSize: 'var(--font-size-xs)', letterSpacing: 0 }}>· non salvate</span>}</div>
              {puoModificare && <button style={{ ...s.btnPri, opacity: noteModificate ? 1 : 0.5 }} disabled={!noteModificate || salvandoNote} onClick={salvaNote}><Save size={13} /> {salvandoNote ? 'SALVO…' : 'SALVA NOTE'}</button>}
            </div>
            <textarea style={s.note} value={note} readOnly={!puoModificare} onChange={e => setNote(e.target.value)}
              placeholder="Scadenza del contratto, referente della struttura, orari concordati, accordi presi a voce…" />
          </div>
        </div>
      )}

      {/* MODAL NUOVA STRUTTURA */}
      {nuova && (
        <div style={s.overlay}>
          <form onSubmit={creaStruttura} style={s.modal}>
            <div style={s.modalTitle}>NUOVA STRUTTURA</div>
            <div style={s.field}><label style={s.label}>NOME</label><input style={s.input} value={nuova.nome} onChange={e => setNuova(n => ({ ...n, nome: e.target.value }))} required autoFocus /></div>
            <div style={s.field}><label style={s.label}>INDIRIZZO (opzionale)</label><input style={s.input} value={nuova.indirizzo} onChange={e => setNuova(n => ({ ...n, indirizzo: e.target.value }))} /></div>
            <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', margin: 0 }}>Usa lo stesso nome della sede nel Planning, così si riconosce subito.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" style={s.btnSec} onClick={() => setNuova(null)}>ANNULLA</button>
              <button type="submit" style={s.btnPri}><Check size={13} /> CREA</button>
            </div>
          </form>
        </div>
      )}

      {anteprima && sel && (
        <AnteprimaDocumento doc={anteprima} endpoint={`/strutture/${sel.id}/documenti/${anteprima.id}/download`} onClose={() => setAnteprima(null)} />
      )}
    </div>
  );
}

// Costo dei campi per corsi e tornei: campi fissi, letti dal business plan (02/11/2026).
const costiDa = (st) => ({
  corsi_modalita: st.corsi_modalita || 'ORARIO', corsi_costo_ora: st.corsi_costo_ora ?? '', corsi_forfait: st.corsi_forfait ?? '',
  tornei_modalita: st.tornei_modalita || 'ORARIO', tornei_costo: st.tornei_costo ?? '',
});
function CostiCampi({ sel, puoModificare, onSalvato }) {
  const [f, setF] = useState(() => costiDa(sel));
  const [salvando, setSalvando] = useState(false);
  const orig = costiDa(sel);
  const modificato = Object.keys(f).some(k => String(f[k]) !== String(orig[k]));
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const salva = async () => {
    setSalvando(true);
    try { await api.put(`/strutture/${sel.id}`, f); await onSalvato(); }
    catch (err) { errore(err); }
    finally { setSalvando(false); }
  };
  const scelta = (k, opzioni) => (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {opzioni.map(([v, l]) => (
        <button key={v} type="button" disabled={!puoModificare} onClick={() => set(k, v)} style={{ ...s.pill, padding: '6px 10px', ...(f[k] === v ? s.pillOn : {}) }}>{l}</button>
      ))}
    </div>
  );
  const campoEuro = (k, placeholder) => (
    <input style={{ ...s.input, width: '130px' }} type="number" step="0.01" min="0" placeholder={placeholder} disabled={!puoModificare} value={f[k]} onChange={e => set(k, e.target.value)} />
  );
  return (
    <div style={{ ...s.card, gridColumn: '1 / -1' }}>
      <div style={s.cardHead}>
        <div style={s.cardTitle}>COSTO DEI CAMPI {modificato && <span style={{ color: '#F59E0B', fontSize: 'var(--font-size-xs)', letterSpacing: 0 }}>· non salvato</span>}</div>
        {puoModificare && <button style={{ ...s.btnPri, opacity: modificato ? 1 : 0.5 }} disabled={!modificato || salvando} onClick={salva}><Save size={13} /> {salvando ? 'SALVO…' : 'SALVA'}</button>}
      </div>
      <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', margin: '0 0 12px' }}>
        Questi valori li usa il <b>Business plan</b> per la sede <b>{sel.nome}</b> (il nome deve essere uguale a quello della sede nel Planning). Lascia vuoto se non lo sai ancora.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
        <div style={{ ...s.riga, flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <b style={{ fontSize: 'var(--font-size-sm)' }}>CORSI</b>
          {scelta('corsi_modalita', [['ORARIO', 'A SLOT (LEZIONE)'], ['FORFAIT', 'CIFRA FISSA A STAGIONE']])}
          {f.corsi_modalita === 'ORARIO'
            ? <label style={s.field}><span style={s.label}>€ PER UNO SLOT DA 1,5 H SU UN CAMPO</span>{campoEuro('corsi_costo_ora', 'es. 27')}</label>
            : <>
                <label style={s.field}><span style={s.label}>€ PER TUTTO L'ANNO</span>{campoEuro('corsi_forfait', 'es. 20000')}<span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>Non dipende da quanti gruppi o ore fate.</span></label>
                <label style={s.field}><span style={s.label}>VALORE DI UNO SLOT (€)</span>{campoEuro('corsi_costo_ora', 'es. 27')}<span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>Il Business plan addebita ai corsi solo gli slot usati; il resto sono campi liberi da sfruttare.</span></label>
              </>}
        </div>
        <div style={{ ...s.riga, flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <b style={{ fontSize: 'var(--font-size-sm)' }}>TORNEI</b>
          {scelta('tornei_modalita', [['ORARIO', 'A ORE PER CAMPO'], ['GIORNATA', 'A GIORNATA']])}
          <label style={s.field}><span style={s.label}>{f.tornei_modalita === 'ORARIO' ? '€ A ORA PER UN CAMPO' : '€ PER UNA GIORNATA DI TORNEO'}</span>{campoEuro('tornei_costo', f.tornei_modalita === 'ORARIO' ? 'es. 20' : 'es. 300')}<span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>0 = campi gratis per i tornei.</span></label>
        </div>
      </div>
    </div>
  );
}

function FormVoce({ voce, setVoce, onSubmit }) {
  const set = (k) => (e) => setVoce(v => ({ ...v, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} style={{ ...s.riga, flexWrap: 'wrap', borderColor: '#F4600C' }}>
      <input style={{ ...s.input, flex: '2 1 140px' }} placeholder="Voce (es. Campo singolo)" value={voce.etichetta} onChange={set('etichetta')} required autoFocus />
      <input style={{ ...s.input, flex: '1 1 90px' }} placeholder="€" type="number" step="0.01" min="0" value={voce.importo} onChange={set('importo')} />
      <input style={{ ...s.input, flex: '2 1 160px' }} placeholder="Dettaglio (es. all'ora, IVA inclusa)" value={voce.dettaglio} onChange={set('dettaglio')} />
      <div style={{ display: 'flex', gap: '4px' }}>
        <button type="submit" style={{ ...s.miniBtn, color: '#22C55E' }} title="Salva"><Check size={13} /></button>
        <button type="button" style={s.miniBtn} onClick={() => setVoce(null)} title="Annulla"><X size={13} /></button>
      </div>
    </form>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px', maxWidth: '620px' },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '18px' },
  pill: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '999px', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
  pillOn: { background: '#F4600C', borderColor: '#F4600C', color: '#fff' },
  pillCount: { display: 'flex', alignItems: 'center', gap: '2px', opacity: 0.8, fontSize: '10px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', minWidth: 0 },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' },
  cardTitle: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: '700', letterSpacing: '2px' },
  nomeSede: { fontFamily: "'Barlow Condensed'", fontSize: '26px', fontWeight: '800', letterSpacing: '1px' },
  vuoto: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', padding: '8px 0' },
  riga: { display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '10px 12px', margin: 0 },
  note: { width: '100%', minHeight: '320px', boxSizing: 'border-box', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', lineHeight: 1.6, outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  iconBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '7px 9px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 700 },
  miniBtn: { background: 'transparent', border: '1px solid var(--border2)', borderRadius: '6px', padding: '5px 6px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  btnPri: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F4600C', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  btnSec: { background: 'var(--border)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { background: 'var(--bg-card)', border: '1px solid #222', borderRadius: '14px', padding: '24px', maxWidth: '480px', width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' },
  modalTitle: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: '700', letterSpacing: '2px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: '700', color: 'var(--text-dim)' },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box' },
};
